import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import SuiteIcon from '../SuiteIcon';

import { useLanguage } from '../../context/LanguageContext';
/**
 * EcosystemBanner
 * Small promo banner for sister-products: Trust Design (TD) and TVD.
 * Shows after the Final CTA section on the landing page.
 */
const EcosystemBanner = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';

  return (
    <section
      className={`py-12 ${isDark ? 'bg-[#0d1117]' : 'bg-white'}`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="ecosystem-banner"
    >
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`relative overflow-hidden rounded-2xl border ${
            isDark
              ? 'bg-gradient-to-r from-navy-700 via-indigo-900/40 to-navy-700 border-white/10'
              : 'bg-gradient-to-r from-indigo-50 via-amber-50 to-indigo-50 border-indigo-100'
          }`}
        >
          {/* Ambient glow */}
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            aria-hidden
            style={{
              background:
                'radial-gradient(400px 200px at 15% 50%, rgba(79,70,229,0.15), transparent 60%),' +
                'radial-gradient(400px 200px at 85% 50%, rgba(245,158,11,0.15), transparent 60%)',
            }}
          />

          <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* Sparkle icon */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-2xl ${isDark ? 'bg-white/10' : 'bg-white'} flex items-center justify-center shadow-sm`}>
              <Sparkles className="w-6 h-6 text-amber-500" />
            </div>

            {/* Copy */}
            <div className="flex-1 text-center sm:text-start">
              <p className={`text-sm sm:text-base font-bold leading-relaxed ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {localize({ ar: 'هل أنهيت درسك؟', en: 'Finished your lesson?' })}
              </p>
              <p className={`text-xs sm:text-sm mt-1 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {localize({ ar: 'حوّل قصتك إلى', en: 'Transform your story into a' })}{' '}
                <span className="inline-flex items-center gap-1 font-bold text-indigo-600 dark:text-indigo-300">
                  <SuiteIcon app="td" size={14} />
                  {localize({ ar: 'كتاب مع Trust Design (TD)', en: 'book with Trust Design (TD)' })}
                </span>
                {' '}{localize({ ar: 'أو إلى', en: 'or a' })}{' '}
                <span className="inline-flex items-center gap-1 font-bold text-amber-600 dark:text-amber-300">
                  <SuiteIcon app="tvd" size={14} />
                  {localize({ ar: 'فيلم مع TVD', en: 'movie with TVD' })}
                </span>
                {isAr ? '.' : '!'}
              </p>
            </div>

            {/* Action buttons — clear external links, target=_blank */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href="https://td-trustdesign.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  // Force open in new tab to bypass any SPA interception
                  e.preventDefault();
                  e.stopPropagation();
                  window.open('https://td-trustdesign.com', '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-[0_4px_14px_rgba(79,70,229,0.25)] transition-all hover:-translate-y-0.5"
                data-testid="td-banner-link"
              >
                <SuiteIcon app="td" size={16} />
                <span>TD</span>
                <ArrowRight className={`w-3 h-3 ${isAr ? 'rotate-180' : ''}`} />
              </a>
              <a
                href="https://trusttvd.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open('https://trusttvd.com', '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold shadow-[0_4px_14px_rgba(245,158,11,0.25)] transition-all hover:-translate-y-0.5"
                data-testid="tvd-banner-link"
              >
                <SuiteIcon app="tvd" size={16} />
                <span>TVD</span>
                <ArrowRight className={`w-3 h-3 ${isAr ? 'rotate-180' : ''}`} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemBanner;
